import { useEffect, useMemo, useState } from 'react';
import { Button as RACButton } from 'react-aria-components';

import { useBridgeApi } from '@/api/BridgeApi';
import { usePendingCounts } from '@/hooks/usePendingCounts';
import { CATEGORY_LABEL } from '@/spaces/chat/insightCategoryLabels';
import {
	jobFindTitle,
	useJobInspirationChat,
} from '@/spaces/chat/useJobInspirationChat';
import type { CuratedInsight } from '@/types/inbox';
import { cn } from '@/utils/cn';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { trackEvent } from '@/utils/fullstory';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';
import type { AgentActivity } from '../../../../bridge/types/state.ts';

type FeedItem =
	| { kind: 'insight'; created_at: string; data: CuratedInsight }
	| { kind: 'activity'; created_at: string; data: AgentActivity }
	| {
			kind: 'job_inspiration';
			created_at: string;
			data: InspirationReference;
	  };

// One cycle's worth. The job saves up to this many posts a day, every day, so
// an uncapped list would bury every insight and activity within a week.
//
// The server caps too, in the job-inspirations route's `limit` default. This
// second cap is the guard for a route that later returns more, not a duplicate:
// the feed's own budget must not depend on someone else's default.
const MAX_JOB_INSPIRATION_ROWS = 3;

const INSTAGRAM_CONNECTION_PROMPT =
	'Help me setup Instagram connection via Express and Facebook';

const InsightRow = ({
	insight,
	isLast,
	onPress,
	isDisabled,
}: {
	insight: CuratedInsight;
	isLast: boolean;
	onPress: () => void;
	isDisabled: boolean;
}) => (
	<RACButton
		onPress={onPress}
		isDisabled={isDisabled}
		className={cn(
			'-mx-1 flex cursor-pointer gap-3 rounded-lg px-1 py-0.5 outline-none',
			'hover:bg-black/[0.02]',
			'disabled:cursor-not-allowed disabled:opacity-50',
		)}
	>
		<div className="flex flex-col items-center pt-[3px]">
			<div className="h-1.5 w-1.5 shrink-0 rounded-full bg-(--text-disabled)" />
			{!isLast && <div className="mt-1.5 w-px flex-1 bg-(--glass-border)" />}
		</div>

		<div
			className={cn(
				'flex min-w-0 flex-col gap-1 text-left',
				isLast ? 'pb-0' : 'pb-5',
			)}
		>
			<span
				className="text-[0.625rem] font-semibold tracking-wide
					text-(--text-disabled) uppercase"
			>
				{CATEGORY_LABEL[insight.category]}
			</span>
			<span className="text-sm break-words text-(--text-secondary)">
				{insight.title}
			</span>
			<span className="mt-0.5 text-[0.625rem] text-(--text-disabled)">
				{formatRelativeTime(insight.created_at)}
			</span>
		</div>
	</RACButton>
);

const activityDisplay = (
	activity: AgentActivity,
): { label: string; title: string } => {
	switch (activity.type) {
		case 'business_profile_ready':
			return { label: 'ONBOARDING', title: 'Business Profile is ready' };
		case 'instagram_connection_nudge':
			return { label: 'SETUP', title: 'Connect Instagram' };
	}
};

const ActivityRow = ({
	activity,
	isLast,
	onPress,
	isDisabled,
}: {
	activity: AgentActivity;
	isLast: boolean;
	onPress: () => void;
	isDisabled: boolean;
}) => {
	const { label, title } = activityDisplay(activity);
	return (
		<RACButton
			onPress={onPress}
			isDisabled={isDisabled}
			className={cn(
				'-mx-1 flex cursor-pointer gap-3 rounded-lg px-1 py-0.5 outline-none',
				'hover:bg-black/[0.02]',
				'disabled:cursor-not-allowed disabled:opacity-50',
			)}
		>
			<div className="flex flex-col items-center pt-[3px]">
				<div className="h-1.5 w-1.5 shrink-0 rounded-full bg-(--text-disabled)" />
				{!isLast && <div className="mt-1.5 w-px flex-1 bg-(--glass-border)" />}
			</div>

			<div
				className={cn(
					'flex min-w-0 flex-col gap-1 text-left',
					isLast ? 'pb-0' : 'pb-5',
				)}
			>
				<span
					className="text-[0.625rem] font-semibold tracking-wide
						text-(--text-disabled) uppercase"
				>
					{label}
				</span>
				<span className="text-sm break-words text-(--text-secondary)">
					{title}
				</span>
				<span className="mt-0.5 text-[0.625rem] text-(--text-disabled)">
					{formatRelativeTime(activity.created_at)}
				</span>
			</div>
		</RACButton>
	);
};

const JobInspirationRow = ({
	inspiration,
	isLast,
	isDisabled,
	onOpen,
}: {
	inspiration: InspirationReference;
	isLast: boolean;
	isDisabled: boolean;
	// The row owns the opener and the feed owns the in-flight latch, so the row
	// hands its opener up rather than calling it directly.
	onOpen: (open: () => void) => void;
}) => {
	// One artifact per row, so the hook lives here rather than in the list loop.
	const openChat = useJobInspirationChat(inspiration);
	// The eyebrow stays a TYPE label, like the other two rows.
	const title = jobFindTitle(inspiration);
	return (
		<RACButton
			onPress={() => onOpen(openChat)}
			isDisabled={isDisabled}
			className={cn(
				'-mx-1 flex cursor-pointer gap-3 rounded-lg px-1 py-0.5 outline-none',
				'hover:bg-black/[0.02]',
				'disabled:cursor-not-allowed disabled:opacity-50',
			)}
		>
			<div className="flex flex-col items-center pt-[3px]">
				<div className="h-1.5 w-1.5 shrink-0 rounded-full bg-(--text-disabled)" />
				{!isLast && <div className="mt-1.5 w-px flex-1 bg-(--glass-border)" />}
			</div>

			<div
				className={cn(
					'flex min-w-0 flex-col gap-1 text-left',
					isLast ? 'pb-0' : 'pb-5',
				)}
			>
				<span
					className="text-[0.625rem] font-semibold tracking-wide
						text-(--text-disabled) uppercase"
				>
					SIMILAR BUSINESS POST
				</span>
				<span className="text-sm break-words text-(--text-secondary)">
					{title}
				</span>
				<span className="mt-0.5 text-[0.625rem] text-(--text-disabled)">
					{formatRelativeTime(inspiration.content.shared_at)}
				</span>
			</div>
		</RACButton>
	);
};

// How long a press holds the feed's rows disabled when the navigation itself
// never clears the latch. Long enough to cover a double-tap, short enough that a
// customer never meets a row that refuses to open.
const PRESS_LATCH_RELEASE_MS = 1000;

export const AgentActivityFeed = ({
	insights: insightsProp,
}: {
	insights?: CuratedInsight[];
} = {}) => {
	const bridgeApi = useBridgeApi();
	const { curatedInsights, jobInspirations, refresh } = usePendingCounts();
	const insights = insightsProp ?? curatedInsights;
	const activities = bridgeApi.user_state?.agent_activity;
	const [isPending, setIsPending] = useState(false);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;

	useEffect(() => {
		setIsPending(false);
		void refresh();
	}, [currentChatId, refresh]);

	// The effect above releases the latch when the press opens a DIFFERENT
	// thread. A press that returns to the thread the customer is already in
	// leaves current_chat_id unchanged, so it never fires, and every row in the
	// feed would stay disabled for the rest of the session. Releasing on time
	// covers that, and it cannot depend on where the press landed.
	useEffect(() => {
		if (!isPending) return;
		const timer = setTimeout(() => setIsPending(false), PRESS_LATCH_RELEASE_MS);
		return () => clearTimeout(timer);
	}, [isPending]);

	const feedItems: FeedItem[] = useMemo(() => {
		const merged: FeedItem[] = [
			...insights.map(
				(i) =>
					({
						kind: 'insight' as const,
						created_at: i.created_at,
						data: i,
					}) satisfies FeedItem,
			),
			...(activities ?? []).map(
				(a) =>
					({
						kind: 'activity' as const,
						created_at: a.created_at,
						data: a,
					}) satisfies FeedItem,
			),
			// Capped BEFORE the merge, so a week of daily finds cannot crowd out
			// the insights and activities beside them.
			...(jobInspirations ?? []).slice(0, MAX_JOB_INSPIRATION_ROWS).map(
				(a) =>
					({
						kind: 'job_inspiration' as const,
						created_at: a.content.shared_at,
						data: a,
					}) satisfies FeedItem,
			),
		];
		merged.sort((a, b) =>
			a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
		);
		return merged;
	}, [insights, activities, jobInspirations]);

	const handleInsightPress = (insight: CuratedInsight) => {
		if (isPending) return;
		trackEvent('Insight Clicked', {
			insight_id: insight.id,
			insight_category: insight.category,
		});
		setIsPending(true);
		bridgeApi.sendAction({
			type: 'do_create_chat',
			chat_type: 'artifact_review',
			initial_message: `Show me this insight: ${insight.title}`,
			linked_item: {
				type: 'curated_insight',
				id: insight.id,
				metadata: {
					title: insight.title,
					description: insight.summary || undefined,
				},
			},
		});
	};

	// Same latch as the two rows below, because this row mints a chat too. The
	// opener arrives from the row, whose hook is bound to one artifact.
	const handleJobInspirationPress = (open: () => void) => {
		if (isPending) return;
		setIsPending(true);
		open();
	};

	const handleActivityPress = (activity: AgentActivity) => {
		if (isPending) return;
		trackEvent('Agent Activity Clicked', { activity_type: activity.type });
		if (activity.type === 'instagram_connection_nudge') {
			setIsPending(true);
			bridgeApi.sendAction({
				type: 'do_create_chat',
				initial_message: INSTAGRAM_CONNECTION_PROMPT,
			});
			return;
		}
		bridgeApi.sendAction({
			type: 'do_navigate_to_space',
			space_type: 'brand',
		});
	};

	return (
		<div className="flex h-full w-full flex-col">
			<span
				className="block pb-3 text-[0.625rem] font-semibold tracking-widest
					text-(--text-disabled) uppercase"
			>
				Recent activity
			</span>
			{feedItems.length === 0 ? (
				<span className="text-xs text-(--text-disabled)">
					No recent activity
				</span>
			) : (
				<div
					className="scrollbar-hidden flex flex-1 flex-col overflow-x-hidden
						overflow-y-auto"
				>
					{feedItems.map((item, i) => {
						const isLast = i === feedItems.length - 1;
						if (item.kind === 'job_inspiration') {
							return (
								<JobInspirationRow
									key={`${item.kind}:${item.data.id}`}
									inspiration={item.data}
									isLast={isLast}
									isDisabled={isPending}
									onOpen={handleJobInspirationPress}
								/>
							);
						}
						if (item.kind === 'insight') {
							return (
								<InsightRow
									key={`${item.kind}:${item.data.id}`}
									insight={item.data}
									isLast={isLast}
									onPress={() => handleInsightPress(item.data)}
									isDisabled={isPending}
								/>
							);
						}
						return (
							<ActivityRow
								key={`${item.kind}:${item.data.id}`}
								activity={item.data}
								isLast={isLast}
								onPress={() => handleActivityPress(item.data)}
								isDisabled={isPending}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
};
